import { useMutation, useQueryClient, mutationOptions } from '@tanstack/react-query';

import {
	createSmtpAccount,
	updateSmtpAccount,
	deleteEmailAccount,
	testSmtpConnection,
} from '../../api/emailAccountsService';

import { emailAccountsKeys, emailAccountsMutationKeys } from './keys';

/**
 * Mutation for creating new SMTP email account
 */
export const createEmailAccountOptions = (queryClient) =>
	mutationOptions({
		mutationKey: emailAccountsMutationKeys.create,
		mutationFn: (payload) => createSmtpAccount(payload),
		onSuccess: () => {
			// Invalidate all accounts queries to refetch
			queryClient.invalidateQueries({ queryKey: emailAccountsKeys.all });
		},
	});

export function useCreateEmailAccount() {
	const qc = useQueryClient();
	return useMutation(createEmailAccountOptions(qc));
}

/**
 * Mutation for updating SMTP email account
 */
export const updateEmailAccountOptions = (queryClient) =>
	mutationOptions({
		mutationKey: emailAccountsMutationKeys.update,
		mutationFn: ({ accountId, ...data }) => updateSmtpAccount(accountId, data),
		onSuccess: (data, variables) => {
			// Invalidate all accounts queries
			queryClient.invalidateQueries({ queryKey: emailAccountsKeys.all });
			// Invalidate specific account query
			if (variables.accountId) {
				queryClient.invalidateQueries({ queryKey: emailAccountsKeys.detail(variables.accountId) });
			}
		},
	});

export function useUpdateEmailAccount() {
	const qc = useQueryClient();
	return useMutation(updateEmailAccountOptions(qc));
}

/**
 * Mutation for deleting email account
 */
export const deleteEmailAccountOptions = (queryClient) =>
	mutationOptions({
		mutationKey: emailAccountsMutationKeys.delete,
		mutationFn: (accountId) => deleteEmailAccount(accountId),
		onSuccess: () => {
			// Invalidate all accounts queries
			queryClient.invalidateQueries({ queryKey: emailAccountsKeys.all });
		},
	});

export function useDeleteEmailAccount() {
	const qc = useQueryClient();
	return useMutation(deleteEmailAccountOptions(qc));
}

/**
 * Mutation for testing SMTP connection
 */
export const testSmtpConnectionOptions = () =>
	mutationOptions({
		mutationKey: emailAccountsMutationKeys.testConnection,
		mutationFn: (data) => testSmtpConnection(data),
		// No cache invalidation needed - this is just a test
	});

export function useTestSmtpConnection() {
	return useMutation(testSmtpConnectionOptions());
}
